import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Castle,
  Users,
  Calendar,
  Bike,
  Footprints,
  Facebook,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Phone,
  Mail,
  ArrowRight,
  Mountain,
  History,
  Heart,
  Star,
  ZoomIn,
  X,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import Section from "@/components/Section";
import SectionHeader from "@/components/SectionHeader";
import Stats from "@/components/Stats";
import { Button } from "@/components/Button";

// ============================================
// IMMAGINI
// ============================================

// Slider Homepage
interface SliderImage {
  src: string;
  title: string;
  subtitle?: string;
  cta?: { text: string; link: string };
  isBanner?: boolean;
  bgColor?: string;
  link?: string;
}

const SLIDER_IMAGES: SliderImage[] = [
  {
    src: "/images/home-slider-01-tesseramento-2026.jpg",
    title: "Tesseramento 2026",
    isBanner: true,
    bgColor: "bg-[#1a365d]",
    link: "/tesseramento",
  },
  {
    src: "/images/home-slider-03-rovine-albero.jpg",
    title: "Alla Scoperta dei Castelli",
    subtitle: "Esplora i borghi fortificati del territorio aquilano",
    cta: { text: "Scopri i Castelli", link: "/castelli" },
  },
  {
    src: "/images/home-slider-04-castello-torre.jpg",
    title: "Storia e Natura",
    subtitle: "Percorsi tra rovine medievali e paesaggi mozzafiato",
    cta: { text: "Vedi le Attività", link: "/attivita" },
  },
  {
    src: "/images/home-slider-06-mura-pietra.jpg",
    title: "Patrimonio da Valorizzare",
    subtitle: "Strutture difensive che raccontano secoli di storia",
    cta: { text: "Chi Siamo", link: "/associazione" },
  },
  {
    src: "/images/home-slider-07-rocca-fiori.jpg",
    title: "Rocca Calascio",
    subtitle: "Uno dei castelli più iconici d'Abruzzo",
    cta: { text: "Esplora la Mappa", link: "/castelli" },
  },
];

// Galleria foto attività
const GALLERY_IMAGES = [
  { src: "/images/associazione-01-gruppo-montagna.jpg", alt: "Gruppo escursionisti in montagna" },
  { src: "/images/associazione-02-selfie-neve.jpg", alt: "Escursione invernale nella neve" },
  { src: "/images/associazione-03-ciclisti-castello.jpg", alt: "Ciclisti davanti a un castello" },
  { src: "/images/associazione-04-gruppo-sassi.jpg", alt: "Pausa durante escursione" },
  { src: "/images/associazione-05-selfie-due-neve.jpg", alt: "Escursione sulla neve" },
  { src: "/images/associazione-06-ciclisti-mare.jpg", alt: "Ciclisti Pedalo Ma Non Troppo" },
  { src: "/images/attivita-foto-anfiteatro-amiternum.jpg", alt: "Anfiteatro di Amiternum" },
];

// Galleria Chi Siamo (sottoinsieme per la sezione)
const CHI_SIAMO_IMAGES = [
  { src: "/images/associazione-01-gruppo-montagna.jpg", alt: "Gruppo in montagna" },
  { src: "/images/associazione-02-selfie-neve.jpg", alt: "Selfie sulla neve" },
  { src: "/images/associazione-03-ciclisti-castello.jpg", alt: "Ciclisti al castello" },
  { src: "/images/associazione-04-gruppo-sassi.jpg", alt: "Gruppo sui sassi" },
];

// Prossimi eventi
const UPCOMING_EVENTS = [
  {
    date: "6 Aprile 2025",
    title: "Castello di Cascina",
    type: "Camminata",
    image: "/images/attivita-2025-04-06-cascina.jpg",
  },
  {
    date: "23 Aprile 2025",
    title: "Rocca di San Marco",
    type: "Passeggiata",
    image: "/images/attivita-2025-04-23-rocca-san-marco.jpg",
  },
  {
    date: "18 Maggio 2025",
    title: "Castiglione di Tornimparte",
    type: "Camminata",
    image: "/images/attivita-2025-05-18-castiglione-tornimparte.jpg",
  },
  {
    date: "25 Maggio 2025",
    title: "Cicloturistica L'Aquila",
    type: "Pedalata",
    image: "/images/attivita-2025-05-25-cicloturistica-aquila-2ed.jpg",
  },
];

// Stats
const stats = [
  { value: "99", label: "Castelli da esplorare", icon: <Castle size={28} /> },
  { value: "XIII", label: "Secolo di storia", icon: <History size={28} /> },
  { value: "500+", label: "Soci attivi", icon: <Users size={28} /> },
  { value: "50+", label: "Eventi all'anno", icon: <Calendar size={28} /> },
];

// ============================================
// COMPONENTI
// ============================================

// Hero Slider Component
const HeroSlider: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % SLIDER_IMAGES.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + SLIDER_IMAGES.length) % SLIDER_IMAGES.length);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, nextSlide]);

  return (
    <div
      className="relative w-full"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      {/* Slides Wrapper */}
      <div className="relative overflow-hidden">
        <div
          className="flex transition-transform duration-700 ease-in-out"
          style={{ transform: `translateX(-${currentSlide * 100}%)` }}
        >
          {SLIDER_IMAGES.map((slide, index) => (
            <div key={index} className="w-full flex-shrink-0">
              {slide.isBanner ? (
                /* BANNER - Immagine intera con CTA */
                <div className="relative">
                  <Link to={slide.link || "/"} className="block">
                    <img src={slide.src} alt={slide.title} className="w-full h-auto" />
                  </Link>
                  {/* CTA Button posizionato vicino al logo */}
                  <Link
                    to="/tesseramento"
                    className="absolute bottom-[18%] left-[50%] -translate-x-1/2 sm:left-[42%] sm:translate-x-0 inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white px-4 py-2 sm:px-6 sm:py-3 md:px-8 md:py-4 rounded-lg font-bold text-sm sm:text-base md:text-lg transition-all hover:scale-105 shadow-xl"
                  >
                    Diventa Socio 2026
                    <ArrowRight size={20} />
                  </Link>
                </div>
              ) : (
                /* HERO - Immagine ritagliata con testo */
                <div className="relative aspect-[16/9] md:aspect-[21/9]">
                  <img src={slide.src} alt={slide.title} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-black/10" />
                  <div className="absolute inset-0 flex items-center justify-center p-6">
                    <div className="text-center text-white max-w-3xl">
                      <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-heading font-bold mb-3 md:mb-4 drop-shadow-lg">
                        {slide.title}
                      </h2>
                      <p className="text-sm sm:text-base md:text-xl mb-4 md:mb-6 text-white/90 drop-shadow max-w-2xl mx-auto">
                        {slide.subtitle}
                      </p>
                      {slide.cta && (
                        <Link
                          to={slide.cta.link}
                          className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white px-5 py-2.5 md:px-7 md:py-3 rounded-lg font-semibold text-sm md:text-base transition-all hover:scale-105 shadow-lg"
                        >
                          {slide.cta.text}
                          <ArrowRight size={18} />
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Frecce */}
      <button
        onClick={prevSlide}
        className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-gray-700 shadow-md transition-all"
        aria-label="Precedente"
      >
        <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 sm:w-11 sm:h-11 flex items-center justify-center rounded-full bg-white/80 hover:bg-white text-gray-700 shadow-md transition-all"
        aria-label="Successiva"
      >
        <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
      </button>

      {/* Indicatori */}
      <div className="absolute bottom-3 sm:bottom-5 left-1/2 -translate-x-1/2 z-10 flex gap-1.5 sm:gap-2">
        {SLIDER_IMAGES.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
              index === currentSlide ? "bg-primary w-5 sm:w-7" : "bg-primary/50 hover:bg-primary/70 w-1.5 sm:w-2"
            }`}
            aria-label={`Slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

// Event Card Component
// ============================================
// LIGHTBOX COMPONENT
// ============================================

interface LightboxProps {
  image: string;
  title: string;
  onClose: () => void;
}

const Lightbox: React.FC<LightboxProps> = ({ image, title, onClose }) => {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "unset";
    };
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
        aria-label="Chiudi"
      >
        <X size={32} className="text-white" />
      </button>

      <div className="absolute top-4 left-4 z-10 max-w-[70%]">
        <p className="text-white text-xl font-heading font-bold drop-shadow-lg">{title}</p>
      </div>

      <motion.img
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3 }}
        src={image}
        alt={title}
        className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
        Premi ESC o clicca fuori per chiudere
      </div>
    </motion.div>
  );
};

// ============================================
// GALLERY LIGHTBOX COMPONENT (con navigazione)
// ============================================

interface GalleryLightboxProps {
  images: { src: string; alt: string }[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

const GalleryLightbox: React.FC<GalleryLightboxProps> = ({ images, currentIndex, onClose, onNavigate }) => {
  const currentImage = images[currentIndex];

  const goToPrev = useCallback(() => {
    onNavigate((currentIndex - 1 + images.length) % images.length);
  }, [currentIndex, images.length, onNavigate]);

  const goToNext = useCallback(() => {
    onNavigate((currentIndex + 1) % images.length);
  }, [currentIndex, images.length, onNavigate]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goToPrev();
      if (e.key === "ArrowRight") goToNext();
    };
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [onClose, goToPrev, goToNext]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
        aria-label="Chiudi"
      >
        <X size={32} className="text-white" />
      </button>

      {/* Title */}
      <div className="absolute top-4 left-4 z-10 max-w-[60%]">
        <p className="text-white text-xl font-heading font-bold drop-shadow-lg">{currentImage.alt}</p>
      </div>

      {/* Counter */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
        <p className="text-white/80 text-sm font-medium">
          {currentIndex + 1} / {images.length}
        </p>
      </div>

      {/* Previous Button */}
      <button
        onClick={(e) => { e.stopPropagation(); goToPrev(); }}
        className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all"
        aria-label="Precedente"
      >
        <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8" />
      </button>

      {/* Next Button */}
      <button
        onClick={(e) => { e.stopPropagation(); goToNext(); }}
        className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all"
        aria-label="Successiva"
      >
        <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8" />
      </button>

      {/* Image */}
      <motion.img
        key={currentImage.src}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3 }}
        src={currentImage.src}
        alt={currentImage.alt}
        className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />

      {/* Instructions */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm text-center">
        Usa ← → per navigare • ESC per chiudere
      </div>
    </motion.div>
  );
};

// ============================================
// EVENT CARD COMPONENT
// ============================================

interface UpcomingEvent {
  date: string;
  title: string;
  type: string;
  image: string;
}

const EventCard: React.FC<{
  date: string;
  title: string;
  type: string;
  image: string;
  onImageClick: (event: UpcomingEvent) => void;
}> = ({ date, title, type, image, onImageClick }) => (
  <div className="bg-white rounded-xl overflow-hidden shadow-elegant hover:shadow-elegant-lg transition-all hover:-translate-y-1 group">
    <div
      className="relative h-48 overflow-hidden cursor-pointer"
      onClick={() => onImageClick({ date, title, type, image })}
    >
      <img
        src={image}
        alt={title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
      <div className="absolute top-3 left-3 bg-primary text-white px-3 py-1 rounded-full text-sm font-medium">
        {type}
      </div>
      {/* Icona zoom in alto a destra su hover */}
      <div className="absolute top-3 right-3 p-2 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <ZoomIn size={20} className="text-white" />
      </div>
    </div>
    <div className="p-5">
      <p className="text-secondary font-semibold text-sm mb-2">{date}</p>
      <p className="font-heading font-semibold text-lg text-foreground">{title}</p>
    </div>
  </div>
);

// Feature Card Component
const FeatureCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
}> = ({ icon, title, description, color }) => (
  <div className="bg-white rounded-xl p-8 shadow-elegant hover:shadow-elegant-lg transition-all text-center group">
    <div
      className={`w-16 h-16 mx-auto rounded-full ${color} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}
    >
      {icon}
    </div>
    <h3 className="text-xl font-heading font-semibold text-foreground mb-3">{title}</h3>
    <p className="text-text-light">{description}</p>
  </div>
);

// ============================================
// MAIN COMPONENT
// ============================================

const Home: React.FC = () => {
  const [lightboxEvent, setLightboxEvent] = useState<UpcomingEvent | null>(null);
  const [galleryIndex, setGalleryIndex] = useState<number | null>(null);
  const [chiSiamoIndex, setChiSiamoIndex] = useState<number | null>(null);

  return (
    <PageLayout>
      {/* Lightbox Modal - Eventi */}
      <AnimatePresence>
        {lightboxEvent && (
          <Lightbox image={lightboxEvent.image} title={lightboxEvent.title} onClose={() => setLightboxEvent(null)} />
        )}
      </AnimatePresence>

      {/* Lightbox Modal - Galleria con navigazione */}
      <AnimatePresence>
        {galleryIndex !== null && (
          <GalleryLightbox
            images={GALLERY_IMAGES}
            currentIndex={galleryIndex}
            onClose={() => setGalleryIndex(null)}
            onNavigate={setGalleryIndex}
          />
        )}
      </AnimatePresence>

      {/* Lightbox Modal - Chi Siamo con navigazione */}
      <AnimatePresence>
        {chiSiamoIndex !== null && (
          <GalleryLightbox
            images={CHI_SIAMO_IMAGES}
            currentIndex={chiSiamoIndex}
            onClose={() => setChiSiamoIndex(null)}
            onNavigate={setChiSiamoIndex}
          />
        )}
      </AnimatePresence>

      {/* Hero Slider */}
      <HeroSlider />

      {/* Chi Siamo Section */}
      <Section background="cream" id="chi-siamo">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <div>
              <SectionHeader
                title="Chi Siamo"
                subtitle="Riscopriamo insieme la storia dei leggendari 99 Castelli"
                align="left"
              />

              <div className="prose prose-lg max-w-none text-text-light space-y-5">
                <p className="text-lg leading-relaxed">
                  <strong className="text-foreground">ASD 99 Castelli</strong> nasce dalla volontà di riscoprire i
                  luoghi dove nacque e venne portata avanti l'idea di fondare la{" "}
                  <strong className="text-foreground">città di L'Aquila</strong>.
                </p>

                <p className="leading-relaxed">
                  Una storia unica e particolare che portò nel <strong className="text-foreground">XIII secolo</strong>{" "}
                  alla ribellione al sistema feudale degli abitanti dei leggendari "99 Castelli", vale a dire borghi e
                  ville dotati nella maggior parte dei casi di strutture difensive di diverse tipologie.
                </p>

                <p className="leading-relaxed">
                  La nostra idea è quella di accompagnarvi a visitare quanti più Castelli possibile,{" "}
                  <strong className="text-foreground">in bicicletta e a piedi</strong>, cercando di mantenere viva
                  l'attenzione su questi importanti pezzi di storia, troppo spesso non valorizzati e lasciati
                  abbandonati a se stessi.
                </p>
              </div>

              <div className="flex flex-wrap gap-4 mt-8">
                <Button href="/associazione" variant="primary">
                  Scopri l'Associazione
                </Button>
                <Button href="https://www.facebook.com/99castelli" variant="outline" icon={<Facebook size={18} />}>
                  Seguici su Facebook
                </Button>
              </div>
            </div>

            {/* Images Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div 
                className="relative rounded-xl overflow-hidden shadow-elegant row-span-2 cursor-pointer group"
                onClick={() => setChiSiamoIndex(0)}
              >
                <img
                  src="/images/associazione-01-gruppo-montagna.jpg"
                  alt="Gruppo in montagna"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                <div className="absolute top-3 right-3 p-2 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <ZoomIn size={20} className="text-white" />
                </div>
              </div>
              <div 
                className="relative rounded-xl overflow-hidden shadow-elegant cursor-pointer group"
                onClick={() => setChiSiamoIndex(1)}
              >
                <img
                  src="/images/associazione-02-selfie-neve.jpg"
                  alt="Selfie sulla neve"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                <div className="absolute top-3 right-3 p-2 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <ZoomIn size={20} className="text-white" />
                </div>
              </div>
              <div 
                className="relative rounded-xl overflow-hidden shadow-elegant cursor-pointer group"
                onClick={() => setChiSiamoIndex(2)}
              >
                <img
                  src="/images/associazione-03-ciclisti-castello.jpg"
                  alt="Ciclisti al castello"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                <div className="absolute top-3 right-3 p-2 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <ZoomIn size={20} className="text-white" />
                </div>
              </div>
              <div 
                className="relative rounded-xl overflow-hidden shadow-elegant col-span-2 cursor-pointer group"
                onClick={() => setChiSiamoIndex(3)}
              >
                <img
                  src="/images/associazione-04-gruppo-sassi.jpg"
                  alt="Gruppo sui sassi"
                  className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                <div className="absolute top-3 right-3 p-2 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <ZoomIn size={20} className="text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Come Esploriamo */}
      <Section background="white">
        <SectionHeader title="Come Esploriamo" subtitle="Due modi per scoprire i castelli del territorio aquilano" />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          <FeatureCard
            icon={<Bike size={32} className="text-secondary" />}
            title="In Bicicletta"
            description="Percorsi ciclabili attraverso i borghi storici, adatti a MTB muscolari e a pedalata assistita."
            color="bg-secondary/10"
          />
          <FeatureCard
            icon={<Footprints size={32} className="text-accent" />}
            title="A Piedi"
            description="Escursioni e trekking per esplorare castelli e strutture difensive medievali."
            color="bg-accent/10"
          />
          <FeatureCard
            icon={<Mountain size={32} className="text-primary" />}
            title="Per Tutti i Livelli"
            description="Attività adatte a famiglie, principianti e sportivi esperti."
            color="bg-primary/10"
          />
          <FeatureCard
            icon={<Heart size={32} className="text-red-500" />}
            title="Con Passione"
            description="Guide esperte che condividono la storia e le tradizioni del territorio."
            color="bg-red-50"
          />
        </div>
      </Section>

      {/* Stats Section */}
      <Section background="primary">
        <SectionHeader
          title="I Numeri della Nostra Storia"
          subtitle="Un patrimonio di castelli, borghi e tradizioni da scoprire insieme"
          light
        />
        <Stats stats={stats} light />
      </Section>

      {/* Prossimi Eventi */}
      <Section background="white">
        <SectionHeader title="Prossime Attività" subtitle="Unisciti a noi nelle prossime escursioni e pedalate" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {UPCOMING_EVENTS.map((event, index) => (
            <EventCard key={index} {...event} onImageClick={setLightboxEvent} />
          ))}
        </div>
        <div className="text-center mt-10">
          <Button href="/attivita" variant="secondary" size="lg">
            Vedi Tutte le Attività
            <ArrowRight size={18} className="ml-2" />
          </Button>
        </div>
      </Section>

      {/* La Storia dei 99 Castelli */}
      <Section background="cream">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Map Preview */}
            <div className="order-2 lg:order-1">
              <div className="relative rounded-xl overflow-hidden shadow-elegant-lg">
                <img src="/images/mappa-castelli.jpg" alt="Mappa dei 99 Castelli" className="w-full" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <Link
                    to="/castelli"
                    className="inline-flex items-center gap-2 bg-white text-foreground px-6 py-3 rounded-lg font-semibold hover:bg-primary hover:text-white transition-colors"
                  >
                    <MapPin size={20} />
                    Esplora la Mappa Interattiva
                  </Link>
                </div>
              </div>
            </div>

            {/* Text Content */}
            <div className="order-1 lg:order-2">
              <SectionHeader
                title="La Leggenda dei 99 Castelli"
                subtitle="Una storia unica nel panorama italiano"
                align="left"
              />

              <div className="prose prose-lg max-w-none text-text-light space-y-5">
                <p className="leading-relaxed">
                  Nel <strong className="text-foreground">1254</strong>, gli abitanti di 99 castelli e ville del contado
                  aquilano si unirono in una straordinaria ribellione contro il sistema feudale, dando vita alla
                  fondazione della <strong className="text-foreground">città di L'Aquila</strong>.
                </p>

                <p className="leading-relaxed">
                  Ogni castello contribuì con i propri abitanti, le proprie risorse e la propria identità. Ancora oggi,
                  la città conserva questa eredità:{" "}
                  <strong className="text-foreground">99 chiese, 99 piazze, 99 fontane</strong> – secondo la leggenda –
                  a ricordo di questa unione.
                </p>

                <p className="leading-relaxed">
                  I resti di queste strutture difensive sono sparsi nel territorio circostante:{" "}
                  <strong className="text-foreground">torri, rocche, mura</strong> che raccontano secoli di storia e che
                  noi ci impegniamo a far conoscere e valorizzare.
                </p>
              </div>

              <div className="flex flex-wrap gap-4 mt-8">
                <Button href="/castelli" variant="primary">
                  Scopri i Castelli
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Galleria Fotografica */}
      <Section background="white">
        <SectionHeader title="Le Nostre Avventure" subtitle="Momenti catturati durante le nostre escursioni" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto">
          {GALLERY_IMAGES.map((image, index) => (
            <div
              key={index}
              onClick={() => setGalleryIndex(index)}
              className={`relative overflow-hidden rounded-xl shadow-elegant group cursor-pointer ${
                index === 0 ? "md:row-span-2" : ""
              }`}
            >
              <img
                src={image.src}
                alt={image.alt}
                loading="lazy"
                className={`w-full object-cover transition-transform duration-500 group-hover:scale-110 ${
                  index === 0 ? "h-full min-h-[300px]" : "h-48 md:h-56"
                }`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="absolute top-3 right-3 p-2 bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <ZoomIn size={20} className="text-white" />
              </div>
              <p className="absolute bottom-4 left-4 text-white font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                {image.alt}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* Affiliazione CSI */}
      <Section background="cream">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-elegant">
            <div className="flex justify-center gap-8 mb-8">
              <img src="/images/logo-99castelli.png" alt="ASD 99 Castelli" className="h-20 object-contain" />
              <img src="/images/logo-icona.png" alt="Icona 99 Castelli" className="h-20 object-contain" />
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-4">
              Associazione Sportiva Dilettantistica
            </h2>
            <p className="text-text-light text-lg mb-6">
              Affiliata al <strong className="text-foreground">CSI - Centro Sportivo Italiano</strong>, Comitato di
              L'Aquila. Le nostre attività sono organizzate con guide cicloturistiche certificate e copertura
              assicurativa per tutti i partecipanti.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="flex items-center gap-2 text-text-light">
                <Star className="text-yellow-500" size={20} />
                <span>Guide Certificate CSI</span>
              </div>
              <div className="flex items-center gap-2 text-text-light">
                <Star className="text-yellow-500" size={20} />
                <span>Assicurazione Infortuni</span>
              </div>
              <div className="flex items-center gap-2 text-text-light">
                <Star className="text-yellow-500" size={20} />
                <span>Attività per Tutti</span>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* L'Aquila Cecilia Preview */}
      <Section background="white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Book Cover */}
            <div className="flex justify-center">
              <img
                src="/images/laquila-cecilia-copertina.jpg"
                alt="L'Aquila Cecilia - Copertina"
                className="max-w-sm w-full rounded-xl shadow-elegant-lg"
              />
            </div>

            {/* Text Content */}
            <div>
              <SectionHeader
                title="L'Aquila Cecilia"
                subtitle="Storia favolosa di una Città meravigliosa"
                align="left"
              />

              <div className="prose prose-lg max-w-none text-text-light space-y-5">
                <p className="leading-relaxed">
                  Un libro dedicato ai bambini dai <strong className="text-foreground">4 ai 10 anni</strong> che
                  racconta la storia di L'Aquila attraverso gli occhi di{" "}
                  <strong className="text-foreground">Cecilia</strong>, una piccola aquila che vive sul Gran Sasso.
                </p>

                <p className="leading-relaxed">
                  Scritto da <strong className="text-foreground">Alessandro Serreli</strong>, il libro è disponibile
                  gratuitamente in formato digitale e in versione cartacea a €13.
                </p>
              </div>

              <div className="flex flex-wrap gap-4 mt-8">
                <Button href="/laquila-cecilia" variant="primary">
                  Scopri il Libro
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* CTA Finale */}
      <Section background="primary">
        <div className="text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">Unisciti a Noi!</h2>
          <p className="text-white/90 text-lg mb-8">
            Ogni vostro mi piace, ogni vostro commento e soprattutto la vostra partecipazione alle nostre iniziative
            sarà per noi uno stimolo a migliorare sempre di più!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button href="/tesseramento" variant="secondary" size="lg">
              Diventa Socio 2026
            </Button>
            <Button
              href="https://www.facebook.com/99castelli"
              variant="outline"
              size="lg"
              className="border-white text-white hover:bg-white hover:text-primary"
              icon={<Facebook size={20} />}
            >
              Seguici su Facebook
            </Button>
          </div>

          {/* Contact Info */}
          <div className="flex flex-wrap justify-center gap-6 text-white/90">
            <a href="tel:+393402894567" className="flex items-center gap-2 hover:text-white transition-colors">
              <Phone size={18} />
              340 2894567
            </a>
            <a
              href="mailto:99castelli@gmail.com"
              className="flex items-center gap-2 hover:text-white transition-colors"
            >
              <Mail size={18} />
              99castelli@gmail.com
            </a>
          </div>
        </div>
      </Section>
    </PageLayout>
  );
};

export default Home;
